import LaunchIcon from '@mui/icons-material/Launch';
import { Box, ButtonBase, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { $api } from 'shared/api/api';
import { SavingsSummaryProductDetailsDrawer } from 'widgets/SavingsSummaryReport/ui/SavingsSummaryProductDetailsDrawer';

import type { CatalogItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import type { SpendProductRowDto } from 'entities/ReportSpendSummary';
import type { ExtendedAxiosRequestConfig } from 'shared/api/types';

interface SpendProductDrillInContextHeaderProps {
    sku: string;
    product: SpendProductRowDto | null;
}

export const SpendProductDrillInContextHeader = ({
    sku,
    product,
}: SpendProductDrillInContextHeaderProps) => {
    const { t } = useTranslation();
    const [selectedProductSku, setSelectedProductSku] = useState<string | null>(
        null,
    );
    const [productItem, setProductItem] = useState<CatalogItem | null>(null);

    useEffect(() => {
        let isCancelled = false;

        const fetchProductItem = async () => {
            try {
                const config: ExtendedAxiosRequestConfig = {
                    keysToNotConvert: ['specifications'],
                };
                const response = await $api.get<CatalogItem>(
                    `/v1/catalogs/items/${encodeURIComponent(sku)}`,
                    config,
                );

                if (!isCancelled) {
                    setProductItem(response.data);
                }
            } catch {
                if (!isCancelled) {
                    setProductItem(null);
                }
            }
        };

        void fetchProductItem();

        return () => {
            isCancelled = true;
        };
    }, [sku]);

    const openProductDetails = useCallback(() => {
        setSelectedProductSku(sku);
    }, [sku]);

    const closeProductDetails = useCallback(() => {
        setSelectedProductSku(null);
    }, []);

    const productImageUrl =
        productItem?.imageUrl ?? productItem?.thumbnailUrl ?? null;

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    gap: '24px',
                    alignItems: 'flex-start',
                    marginBottom: '30px',
                    '&:hover .context-open-icon': {
                        opacity: 1,
                    },
                }}
            >
                {productImageUrl ? (
                    <ButtonBase
                        type="button"
                        onClick={openProductDetails}
                        sx={{
                            cursor: 'pointer',
                            flexShrink: 0,
                            borderRadius: '8px',
                            padding: 0,
                            '&:hover': {
                                opacity: 0.9,
                            },
                        }}
                    >
                        <Box
                            component="img"
                            src={productImageUrl}
                            alt={String(
                                product?.itemName ??
                                    t('spendSummary.productDetailsImageAlt'),
                            )}
                            sx={{
                                width: '100px',
                                height: '100px',
                                borderRadius: '8px',
                                backgroundColor: '#FFF',
                                objectFit: 'cover',
                                display: 'block',
                            }}
                        />
                    </ButtonBase>
                ) : (
                    <Box
                        sx={{
                            width: '100px',
                            height: '100px',
                            borderRadius: '8px',
                            backgroundColor: '#FFF',
                            border: '1px solid #DFDFDF',
                            flexShrink: 0,
                            boxSizing: 'border-box',
                        }}
                    />
                )}
                <Box
                    sx={{
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px',
                            color: 'common.black',
                            fontSize: '1.3rem',
                            lineHeight: '2rem',
                            fontWeight: 400,
                        }}
                    >
                        {product?.brand ? <span>{product.brand}</span> : null}
                        <span>{product?.itemSku ?? sku}</span>
                    </Box>
                    <ButtonBase
                        type="button"
                        onClick={openProductDetails}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '10px',
                            cursor: 'pointer',
                            width: 'fit-content',
                            maxWidth: '100%',
                            padding: 0,
                            backgroundColor: 'transparent',
                            textAlign: 'left',
                            '&:hover .product-title-text': {
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        <Typography
                            variant="h1"
                            className="product-title-text"
                            sx={{
                                color: 'common.black',
                                minWidth: 0,
                                wordBreak: 'break-word',
                            }}
                        >
                            {product?.itemName ?? sku}
                        </Typography>
                        <LaunchIcon
                            className="context-open-icon"
                            sx={{
                                fontSize: '2rem',
                                color: 'common.black',
                                flexShrink: 0,
                                opacity: 0,
                                transition: 'opacity 120ms ease-out',
                            }}
                        />
                    </ButtonBase>
                </Box>
            </Box>
            <SavingsSummaryProductDetailsDrawer
                sku={selectedProductSku}
                open={Boolean(selectedProductSku)}
                onClose={closeProductDetails}
                initialItem={productItem}
            />
        </>
    );
};
